import { useCallback, useEffect, useRef, useState } from "react";
import {
  useCustomerDropDownList,
  useGomakeAxios,
  useGomakeRouter,
  useSnackBar,
} from "@/hooks";
import { getAndSetClientTypes } from "@/services/hooks";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  QuoteIfExistState,
  QuoteNumberState,
  homeReportsState,
} from "@/pages-components/quote-new/store/quote";
import {
  createNewDocumentApi,
  getAllReportsApi,
  getIfCartExistApi,
  saveDocumentApi,
} from "@/services/api-service/generic-doc/documents-api";
import { selectedClientState } from "@/pages-components/quotes/states";
import { CLIENT_TYPE_Id } from "@/pages/customers/enums";
import { prevSelectedClientState, ProductCategoryType, mapProductToQuoteTreeNode, buildSelectedQuoteProduct } from "./states";
import { EProductStructureType } from "@/widgets/shared-admin-customers/add-product/settings/settings-data";
import {
  getAllProductAndMaterialsApi,
  addDocumentItemFromProductApi,
} from "@/services/api-service/products/product-endpoints";
import { addDocumentItemFromMaterialRowApi } from "@/services/api-service/materials/materials-endpoints";
import { ETab } from "@/pages-components/admin/home/widgets/quote-widget/enums";
import { EStatisticsLabels } from "@/pages-components/quotes/enums";
import { Permissions } from "@/components/CheckPermission/enum";
import { permissionsState } from "@/store/permissions";
import {
  getAllStuckJobsApi,
  getAllStuckJobsByAgentIdApi,
  getNumberOfJobsWaitingForApprovalApi,
  getNumberOfJobsWaitingForApprovalByAgentIdApi,
} from "@/services/api-service/production-floor/production-floor-endpoints";
import { userState } from "@/store/user";
import { updateQuoteStatusSocketIo } from "@/hooks/socket-io/update-quote-status-socket-io";
import {
  mergeDocumentClientWithCustomerListRow,
  parseVatStatusForProductRoute,
} from "@/utils/helpers";
import {
  getHomeDateRange,
  homeDateFilterState,
} from "@/pages-components/admin/home/home-date-filter";

let latestHomeQuoteCheck = 0;

const useQuoteWidget = () => {
  const { t } = useTranslation();
  const { callApi } = useGomakeAxios();
  const { navigate } = useGomakeRouter();
  const { alertFaultUpdate, alertFault } = useSnackBar();
  const [clientTypesValue, setClientTypesValues] = useState([]);
  const { alertFaultAdded, alertSuccessAdded } = useSnackBar();
  const [userQuote, setUserQuote] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedClientType, setSelectedClientType] = useState<any>({});
  const [selectedClient, setSelectedClient] =
    useRecoilState<any>(selectedClientState);
  const [previousClient, setPreviousClient] = useRecoilState<any>(
    prevSelectedClientState
  );
  const permissions = useRecoilValue(permissionsState);
  const dateFilter = useRecoilValue(homeDateFilterState);
  const setQuoteIfExist = useSetRecoilState<any>(QuoteIfExistState);
  const setAllReports = useSetRecoilState<any>(homeReportsState);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [QuoteId, setQuoteId] = useState("");
  const setQuoteNumber = useSetRecoilState<any>(QuoteNumberState);
  const [selectedTab, setSelectedTab] = useState<ETab>(ETab.PRODUCTS);

  const { renderOptions, checkWhatRenderArray, isFetching, listClient } =
    useCustomerDropDownList({});
  // Skip clearing a customer the user picked after this visit to home started.
  const userChangedClientRef = useRef(false);
  // Home loads products before the open quote sets the customer. A slower
  // response from that first request must not replace the customer list.
  const productsRequestIdRef = useRef(0);

  const onClickOpenModal = (quoteId: any) => {
    setOpenModal(true);
  };

  //PopOver Btns
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const onClickCloseModal = async () => {
    setOpenModal(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const checkVariables = (var1, var2, var3) => {
    if (var1?.id && var2?.id && var3?.id) {
      return false;
    } else {
      return true;
    }
  };

  const handleOpenModal = (newClient) => {
    //setPreviousClient(selectedClient ? selectedClient : previousClient);
    setPreviousClient(selectedClient);
    setSelectedClient(newClient);
    setOpenModal(true);
  };

  const handleCancel = async () => {
    setSelectedClient(previousClient);
    setOpenModal(false);
  };

  const handleClickToSelectedCustomer = async (clientIdIfExist, value) => {
    userChangedClientRef.current = true;
    const quoteClientId = clientIdIfExist ?? null;
    const nextClientId = value?.id ?? null;
    // A quote is already open, and the user picked someone else or cleared the field.
    if (quoteClientId != null && nextClientId !== quoteClientId) {
      handleOpenModal(value ?? null);
      return;
    }
    setSelectedClient(value);
    // Set client type immediately if available
    if (value?.clientTypeId && clientTypesValue?.length > 0) {
      const clientType = clientTypesValue.find(
        (c) => c.id === value.clientTypeId
      );
      if (clientType) {
        setSelectedClientType(clientType);
      }
    }
  };

  const getCustomerType = useCallback(() => {
    if (!selectedClient?.clientTypeId || !clientTypesValue?.length) {
      return null;
    }
    const clientType = clientTypesValue.find(
      (c) => c.id === selectedClient?.clientTypeId
    );
    return clientType || null;
  }, [selectedClient, clientTypesValue]);

  useEffect(() => {
    if (selectedClient?.clientTypeId && clientTypesValue?.length > 0) {
      const clientType = getCustomerType();
      if (clientType) {
        setSelectedClientType(clientType);
      }
    }
  }, [selectedClient, clientTypesValue, getCustomerType]); // Also depend on clientTypesValue

  const getAndSetExistQuote = useCallback(async (clearSelectionIfNoQuote = false) => {
    let existingQuote: any = null;
    const checkId = clearSelectionIfNoQuote ? ++latestHomeQuoteCheck : null;
    const callBack = (res) => {
      if (checkId != null && checkId !== latestHomeQuoteCheck) {
        return;
      }
      if (res?.data?.succ) {
        const { result } = res.data;
        const { id, number, client } = result;
        const mergedClient = mergeDocumentClientWithCustomerListRow(
          client,
          listClient
        );
        const resultWithClient = { ...result, client: mergedClient };
        existingQuote = resultWithClient;
        setUserQuote(resultWithClient);
        setQuoteId(id);
        setQuoteNumber(number);
        setQuoteIfExist(true);
        setSelectedClient(mergedClient);
        const clientType = clientTypesValue.find(
          (c) => c.id === mergedClient?.clientTypeId
        );
        setSelectedClientType(clientType);
      } else if (
        clearSelectionIfNoQuote &&
        res?.success &&
        !userChangedClientRef.current
      ) {
        // Home was left and reopened with no open quote: drop the remembered customer.
        setUserQuote(null);
        setQuoteId(null);
        setQuoteNumber(null);
        setQuoteIfExist(false);
        setSelectedClient(null);
        setSelectedClientType(null);
        setPreviousClient(null);
      }
    };
    await getIfCartExistApi(callApi, callBack, { documentType: 0 });
    return existingQuote;
  }, [
    callApi,
    clientTypesValue,
    listClient,
    setPreviousClient,
    setQuoteIfExist,
    setQuoteNumber,
    setSelectedClient,
  ]);

  // get-exist-document response often omits vatStatus; merge with CRM list row (get-recent / get-all) by id when it arrives
  const clientListMergeSignature = (c: any) =>
    c ? `${c.id}|${c.vatStatus ?? ""}|${c.code ?? ""}|${c.name ?? ""}` : "";

  useEffect(() => {
    if (!userQuote?.client?.id || !listClient?.length) {
      return;
    }
    if (String(selectedClient?.id) !== String(userQuote.client.id)) {
      return;
    }
    if (
      !listClient.some(
        (c) => c && String(c.id) === String(userQuote.client.id)
      )
    ) {
      return;
    }
    const merged = mergeDocumentClientWithCustomerListRow(
      userQuote.client,
      listClient
    );
    if (clientListMergeSignature(merged) === clientListMergeSignature(userQuote.client)) {
      return;
    }
    setSelectedClient(merged);
    setUserQuote((prev) => (prev ? { ...prev, client: merged } : prev));
  }, [listClient, userQuote, selectedClient?.id]);

  const getAllStuckJobs = async () => {
    const range = getHomeDateRange(dateFilter);
    const callBack = (res) => {
      setAllStuckJobs(res?.data);
    };
    await getAllStuckJobsApi(callApi, callBack, {
      fromDate: range.fromDate,
      toDate: range.toDate,
    });
  };
  const getAllStuckJobsByAgentId = async () => {
    const range = getHomeDateRange(dateFilter);
    const callBack = (res) => {
      setAllStuckJobs(res?.data);
    };
    await getAllStuckJobsByAgentIdApi(callApi, callBack, {
      fromDate: range.fromDate,
      toDate: range.toDate,
    });
  };
  const getAllJobsWaitingForApproval = async () => {
    const range = getHomeDateRange(dateFilter);
    const callBack = (res) => {
      setAllJobsWaitingForApproval(res?.data);
    };
    await getNumberOfJobsWaitingForApprovalApi(callApi, callBack, {
      fromDate: range.fromDate,
      toDate: range.toDate,
    });
  };
  const getAllJobsWaitingForApprovalByAgentId = async () => {
    const range = getHomeDateRange(dateFilter);
    const callBack = (res) => {
      setAllJobsWaitingForApproval(res?.data);
    };
    await getNumberOfJobsWaitingForApprovalByAgentIdApi(callApi, callBack, {
      fromDate: range.fromDate,
      toDate: range.toDate,
    });
  };
  // useEffect(() => {
  //   getAllStuckJobs();
  //   getAllStuckJobsByAgentId();
  // }, []);

  const onClickSaveQuote = async (quoteId) => {
    const callBack = (res) => {
      if (res?.success) {
        setQuoteNumber(null);
        setQuoteIfExist(false);
        setUserQuote(null);
      } else {
        alertFaultUpdate();
      }
    };
    await saveDocumentApi(callApi, callBack, {
      documentType: 0,
      document: {
        documentId: quoteId,
      },
    });
  };

  const _renderErrorMessage = () => {
    if (!selectedClient?.id) {
      return t("home.admin.pleaseSelectCustomer");
    }
    if (!selectedClientType?.id) {
      return t("home.admin.pleaseSelectClientType");
    }
    if (!selectedProduct?.id) {
      return t("home.admin.pleaseSelectProduct");
    }
  };

  const getAllClientTypes = useCallback(async () => {
    try {
      await getAndSetClientTypes(callApi, setClientTypesValues, {
        cardType: CLIENT_TYPE_Id.CUSTOMER,
      });
    } catch (error) {
      console.error("Error fetching client types:", error);
    }
  }, [callApi]);

  const [allStuckJobs, setAllStuckJobs] = useState(0);
  const [allJobsWaitingForApproval, setAllJobsWaitingForApproval] = useState(0);
  const user = useRecoilValue<any>(userState);

  const { } = updateQuoteStatusSocketIo({
    setUserQuote,
    setQuoteId,
    setQuoteNumber,
    setQuoteIfExist,
    setSelectedClient,
    setSelectedClientType,
    clientTypesValue,
    isFromHomePage: true,
  });
  useEffect(() => {
    if (permissions.includes(Permissions.SHOW_ALL_STUCK_JOBS_ANAYTICS)) {
      getAllStuckJobs();
      getAllJobsWaitingForApproval();
    } else {
      if (user?.isAgent) {
        getAllStuckJobsByAgentId();
        getAllJobsWaitingForApprovalByAgentId();
      }
    }
  }, [permissions, dateFilter]);

  const getAllReports = async () => {
    const range = getHomeDateRange(dateFilter);
    const callBack = (res) => {
      if (res?.success) {
        const baseCards = Array.isArray(res?.data) ? res.data : [];
        if (
          !permissions.includes(Permissions.SHOW_ALL_STUCK_JOBS_ANAYTICS) &&
          !user?.isAgent
        ) {
          setAllReports(baseCards);
        } else {
          setAllReports([
            ...baseCards,
            {
              key: EStatisticsLabels.JOBS_WAITING_FOR_APPROVAL,
              value: allJobsWaitingForApproval,
            },
            {
              key: EStatisticsLabels.STUCK_JOBS,
              value: allStuckJobs,
            },
          ]);
        }
      }
    };
    await getAllReportsApi(callApi, callBack, {
      customerId: selectedClient?.id,
      productId: selectedProduct?.id,
      fromDate: range.fromDate,
      toDate: range.toDate,
    } as any);
  };

  useEffect(() => {
    getAllProductsAndMaterials();
  }, [selectedClient, selectedClientType, selectedTab]);

  useEffect(() => {
    if (selectedTab === ETab.CUSTOMERS_SKU) {
      setSelectedTab(ETab.PRODUCTS);
    }
  }, [selectedTab]);

  useEffect(() => {
    getAllReports();
  }, [
    selectedClient,
    selectedProduct,
    allStuckJobs,
    allJobsWaitingForApproval,
    dateFilter,
  ]);

  useEffect(() => {
    const isDisabled = checkVariables(
      selectedClientType,
      selectedClient,
      selectedProduct
    );
    setIsDisabled(isDisabled);
  }, [selectedClientType, selectedClient, selectedProduct]);

  //  Add Customer / Search
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [customer, setCustomer] = useState([]);

  const onCustomerAdd = (customer) => {
    handleClickToSelectedCustomer(userQuote?.client?.id, customer).then();
    setOpenCustomerModal(false);
  };

  const onCustomerAddFromOption = (customer) => {
    handleClickToSelectedCustomer(userQuote?.client?.id, customer).then();
  };

  const onClickAddCustomer = () => {
    setOpenCustomerModal(true);
  };
  const onCloseCustomerModal = () => {
    setOpenCustomerModal(false);
  };
  const [products, setProducts] = useState([]);
  const [combinedData, setCombinedData] = useState<any>([]);

  useEffect(() => {
    if (products?.length > 0 && combinedData?.length > 0) {
      const findDefaultProduct = products.find(
        (item) => item.isDefaultInHomePage
      );
      if (findDefaultProduct) {
        handleProductChange(findDefaultProduct?.id);
      }
    }
  }, [combinedData]);

  const getAllProductsAndMaterials = useCallback(async () => {
    const requestId = ++productsRequestIdRef.current;
    const callBack = (res) => {
      if (requestId !== productsRequestIdRef.current) {
        return;
      }
      if (res?.success) {
        setProducts(res?.data?.products);
        const transformProducts = (products) => {
          return {
            value: ETab.PRODUCTS,
            selectable: false,
            title: t("tabs.products"),
            text: t("tabs.products"),
            level: 0,
            children: (products || []).map(mapProductToQuoteTreeNode),
          };
        };

        const transformMaterials = (materials) => {
          return {
            value: ETab.MATERIALS,
            title: t("tabs.materials"),
            text: t("tabs.materials"),
            level: 0,
            selectable: false,
            children: materials.map((material) => ({
              value: material.materialTypeName,
              title: material.materialTypeName,
              text: material.materialTypeName,
              level: 1, // Material Types are level 1
              selectable: false,
              children: material.categories.map((category) => ({
                value: `${material.materialTypeName}-${category.categoryName}`,
                title: category.categoryName,
                text: category.categoryName,
                selectable: false,
                level: 2, // Categories are level 2
                children: category.rows.map((row) => ({
                  value: row.id,
                  title: row.value,
                  text: row.value,
                  level: 3, // Rows are level 3
                  category: ProductCategoryType.MATERIAL,
                })),
              })),
            })),
          };
        };

        const attachParentReferences = (nodes, parent = null) => {
          if (!Array.isArray(nodes)) return [];

          return nodes.map((node) => {
            const newNode = { ...node, parent };
            if (newNode.children) {
              newNode.children = attachParentReferences(
                newNode.children,
                newNode
              );
            }
            return newNode;
          });
        };
        const transformedProducts = transformProducts(res?.data?.products || []);
        const transformedMaterials = transformMaterials(res?.data?.materials || []);
        transformedProducts.children = attachParentReferences(
          transformedProducts.children,
          transformedProducts
        );
        transformedMaterials.children = attachParentReferences(
          transformedMaterials.children,
          transformedMaterials
        );

        // Combine the transformed data
        const combinedFinalData = [transformedProducts, transformedMaterials];
        setCombinedData(combinedFinalData);
      }
    };

    await getAllProductAndMaterialsApi(callApi, callBack, {
      clientId: selectedClient?.id,
      clientTypeId: selectedClientType?.id,
      selectionType: selectedTab,
    });
  }, [selectedClient, selectedClientType, selectedTab]);

  const handleProductChange = (e) => {
    const selectedValue = e;
    // Find the selected node from combinedData
    const findNodeByKey = (key, nodes) => {
      for (const node of nodes) {
        if (node.value === key) {
          return node;
        }
        if (node.children) {
          const found = findNodeByKey(key, node.children);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    const selectedNode = findNodeByKey(selectedValue, combinedData);

    if (selectedNode && !selectedNode.children) {
      let matchedProduct = null;
      const productId =
        selectedNode?.productId || selectedValue;
      if (
        selectedNode?.category === ProductCategoryType.PRODUCT ||
        selectedNode?.category === ProductCategoryType.CUSTOMER_SKU
      ) {
        matchedProduct = products?.find(
          (product) => product.id === productId
        );
      }
      setSelectedProduct(
        buildSelectedQuoteProduct(selectedNode, matchedProduct, selectedValue)
      );
    } else {
      setSelectedProduct(null);
    }
  };

  const addDocumentItemFromMaterial = async () => {
    const callBack = (res) => {
      if (res.success) {
        alertSuccessAdded();
        navigate("/quote");
      } else {
        alertFaultAdded();
      }
    };
    await addDocumentItemFromMaterialRowApi(
      callApi,
      callBack,
      {
        clientId: selectedClient?.id,
        printHouseMaterialId: selectedProduct?.id,
        documentType: 0
      },
    );
  };

  const addDocumentItemFromProduct = async () => {
    const callBack = (res) => {
      if (res.success) {
        alertSuccessAdded();
        navigate("/quote");
      } else {
        alertFaultAdded();
      }
    };
    await addDocumentItemFromProductApi(
      callApi,
      callBack,
      {
        clientId: selectedClient?.id,
        productId: selectedProduct?.id,
      },
      0
    ); // DOCUMENT_TYPE.quote
  };

  const onSelectTab = (v) => {
    setSelectedTab(v);
    localStorage.setItem("homePageSelectedTab", JSON.stringify(v));
  };

  const onClickCreateQuoteForCustomer = (compositeData?: {
    workName?: string;
    quantity?: number;
    compositeProducts?: Array<{
      id: string;
      quantity: number;
      jobName: string;
    }>;
  }) => {
    // Check if product has isMaterial flag or is a material category
    if (selectedProduct?.category === ProductCategoryType.MATERIAL || selectedProduct?.isMaterial === true) {
      addDocumentItemFromMaterial();
      return;
    }

    // If product is empty, use the new endpoint to add document item directly
    if (selectedProduct?.isEmptyProduct === true) {
      addDocumentItemFromProduct();
      return;
    }

    const hasSelectedClient = !!selectedClient?.id;

    if (hasSelectedClient) {
      const clientId = selectedClient.id;
      const clientName = selectedClient.name;
      const clientTypeId = selectedClientType?.id;

      if (!clientId || !clientTypeId || !selectedProduct?.id) {
        const errorMessage =
          !clientId ? t("home.admin.pleaseSelectClient")
            :
            !clientTypeId ? t("home.admin.pleaseSelectClientType")
              :
              !selectedProduct?.id ? t("home.admin.pleaseSelectProduct") : "";
        alertFault(errorMessage);
        return;
      }

      const vatForRoute = parseVatStatusForProductRoute(
        selectedClient?.vatStatus,
        userQuote?.vatStatus,
        userQuote?.client?.vatStatus
      );

      const params = new URLSearchParams();
      params.append("clientTypeId", String(clientTypeId));
      params.append("customerId", String(clientId));
      params.append("customerName", clientName);
      if (vatForRoute !== undefined) {
        params.append("vatStatus", String(vatForRoute));
      }
      params.append("productId", String(selectedProduct.id));
      if (
        selectedProduct?.productStructureType ===
        EProductStructureType.COMPOSITE
      ) {
        // Handle new format with compositeProducts array
        if (
          compositeData?.compositeProducts &&
          Array.isArray(compositeData.compositeProducts)
        ) {
          params.append(
            "compositeProducts",
            JSON.stringify(compositeData.compositeProducts)
          );
        } else {
          // Handle old format for backward compatibility
          if (compositeData?.workName) {
            params.append("workName", compositeData.workName);
          }
          if (typeof compositeData?.quantity === "number") {
            params.append("quantity", String(compositeData.quantity));
          }
        }
      }
      navigate(`/products/create?${params.toString()}`);
    } else {
      // Navigate with just the productId
      if (!selectedProduct?.id) {
        alertFaultUpdate();
        return;
      }

      const params = new URLSearchParams();
      const vatFromQuoteOrUrl = parseVatStatusForProductRoute(
        userQuote?.vatStatus,
        userQuote?.client?.vatStatus
      );
      if (vatFromQuoteOrUrl !== undefined) {
        params.append("vatStatus", String(vatFromQuoteOrUrl));
      }
      params.append("productId", String(selectedProduct.id));
      if (
        selectedProduct?.productStructureType ===
        EProductStructureType.COMPOSITE
      ) {
        // Handle new format with compositeProducts array
        if (
          compositeData?.compositeProducts &&
          Array.isArray(compositeData.compositeProducts)
        ) {
          params.append(
            "compositeProducts",
            JSON.stringify(compositeData.compositeProducts)
          );
        } else {
          // Handle old format for backward compatibility
          if (compositeData?.workName) {
            params.append("workName", compositeData.workName);
          }
          if (typeof compositeData?.quantity === "number") {
            params.append("quantity", String(compositeData.quantity));
          }
        }
      }
      navigate(`/products/create?${params.toString()}`);
    }
  };

  const onclickCreateNew = async () => {
    const callBack = (res) => {
      if (res?.success) {
        navigate("/quote");
      } else {
        alertFaultUpdate();
      }
    };

    await createNewDocumentApi(callApi, callBack, {
      documentType: 0,
      ...(selectedClient?.id && { clientId: selectedClient?.id }),
    });
  };

  const ensureQuoteInCreate = useCallback(async () => {
    const existingQuoteId = userQuote?.id || QuoteId;
    if (existingQuoteId) {
      return existingQuoteId;
    }

    let createdQuoteId: string | null = null;
    const callBack = (res) => {
      if (!res?.success) {
        return;
      }

      createdQuoteId =
        res?.data?.documentId ??
        res?.data?.id ??
        res?.data?.result?.id ??
        null;
    };

    await createNewDocumentApi(callApi, callBack, {
      documentType: 0,
      ...(selectedClient?.id && { clientId: selectedClient?.id }),
    });

    const createdQuote = await getAndSetExistQuote();
    return createdQuote?.id || createdQuoteId;
  }, [userQuote?.id, QuoteId, callApi, selectedClient?.id, getAndSetExistQuote]);

  return {
    clientTypesValue,
    isDisabled,
    id,
    openModal,
    getAndSetExistQuote,
    userQuote,
    open,
    anchorEl,
    selectedClientType,
    handleClick,
    onClickOpenModal,
    selectedProduct,
    handleClose,
    onClickCloseModal,
    setSelectedClientType,
    setSelectedClient,
    selectedClient,
    setSelectedProduct,
    setOpenModal,
    setUserQuote,
    handleClickToSelectedCustomer,
    checkWhatRenderArray,
    renderOptions,
    onClickCreateQuoteForCustomer,
    _renderErrorMessage,
    onClickSaveQuote,
    getAllReports,
    openCustomerModal,
    customer,
    setCustomer,
    onCustomerAdd,
    onClickAddCustomer,
    onCloseCustomerModal,
    handleCancel,
    previousClient,
    QuoteId,
    setQuoteId,
    getAllClientTypes,
    combinedData,
    handleProductChange,
    isFetching,
    selectedTab,
    setSelectedTab,
    onSelectTab,
    listClient,
    navigate,
    onclickCreateNew,
    onCustomerAddFromOption,
    ensureQuoteInCreate,
  };
};

export { useQuoteWidget };
